/**
 * Admin Segurança — the governed session-revocation cache TTL (D342). The AR-2a guard caches an ACTIVE session
 * verdict for this many milliseconds, so it is the window a just-revoked (logged-out / stolen) access token may still
 * be admitted. A super admin changes it at runtime: a **decrease** tightens the control and applies at once; an
 * **increase** loosens it and waits for a second super admin (four-eyes). The server is the authority on every
 * rule (bounds, four-eyes, GLOBAL grant); this module mirrors them so the screen can explain the outcome before the
 * operator confirms. Pure and framework-neutral (tested in Node); locale formatting takes the chrome language as input.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type ApiError,
  type GovernedChangeResponse,
  type GovernedChangeStatus,
  type GovernedSettingChangeView,
  type ProposeSessionRevocationTtlInput,
  type SecuritySettingsApi,
  type SessionRevocationTtlView,
} from '@vello/api-client';

// ---- change form ---------------------------------------------------------------------------------------

export interface TtlChangeForm {
  /** Milliseconds as the operator types them (digits only). */
  readonly valueMs: string;
  /** Why the value changes — recorded on the audit trail. */
  readonly reason: string;
}

export type TtlChangeField = 'valueMs' | 'reason';

export interface TtlChangeIssue {
  readonly field: TtlChangeField;
  readonly code: 'NOT_AN_INTEGER' | 'BELOW_MINIMUM' | 'ABOVE_MAXIMUM' | 'NO_CHANGE' | 'REASON_REQUIRED';
}

export type TtlChangeKind = 'TIGHTEN' | 'LOOSEN';

export interface NormalizedTtlChange extends ProposeSessionRevocationTtlInput {
  /** TIGHTEN applies immediately; LOOSEN is held for a second operator's approval. */
  readonly kind: TtlChangeKind;
  /** The value the change is decided against: the effective value, or the enforced value while the stored one is unreadable. */
  readonly fromMs: number;
}

export function emptyTtlChangeForm(): TtlChangeForm {
  return { valueMs: '', reason: '' };
}

/** The value a change is decided against: the effective value, or (stored value unreadable) the value the runtime enforces. */
export function currentTtlMs(view: SessionRevocationTtlView): number {
  return view.effective.valid ? view.effective.valueMs : view.effective.enforcedValueMs;
}

/**
 * Mirrors the server: moving in the loosening direction is LOOSEN. While the stored value is unreadable, re-writing the
 * enforced value is a repair (TIGHTEN, never UNCHANGED); moving past it is still a four-eyes LOOSEN.
 */
export function classifyTtlChange(view: SessionRevocationTtlView, nextMs: number): TtlChangeKind | 'UNCHANGED' {
  const current = currentTtlMs(view);
  if (nextMs === current) return view.effective.valid ? 'UNCHANGED' : 'TIGHTEN';
  const increases = nextMs > current;
  return increases === (view.loosensWhen === 'INCREASE') ? 'LOOSEN' : 'TIGHTEN';
}

const DIGITS = /^\d+$/;

/** Validate the form against the view's bounds and current value; one issue per field. */
export function normalizeTtlChangeForm(form: TtlChangeForm, view: SessionRevocationTtlView): Result<NormalizedTtlChange, readonly TtlChangeIssue[]> {
  const issues: TtlChangeIssue[] = [];
  const raw = form.valueMs.trim();
  let valueMs: number | undefined;
  let kind: TtlChangeKind | 'UNCHANGED' | undefined;
  if (!DIGITS.test(raw) || !Number.isSafeInteger(Number(raw))) {
    issues.push({ field: 'valueMs', code: 'NOT_AN_INTEGER' });
  } else {
    valueMs = Number(raw);
    if (valueMs < view.bounds.minMs) issues.push({ field: 'valueMs', code: 'BELOW_MINIMUM' });
    else if (valueMs > view.bounds.maxMs) issues.push({ field: 'valueMs', code: 'ABOVE_MAXIMUM' });
    else {
      kind = classifyTtlChange(view, valueMs);
      if (kind === 'UNCHANGED') issues.push({ field: 'valueMs', code: 'NO_CHANGE' });
    }
  }
  const reason = form.reason.trim();
  if (reason === '') issues.push({ field: 'reason', code: 'REASON_REQUIRED' });
  if (issues.length > 0 || valueMs === undefined || kind === undefined || kind === 'UNCHANGED') return err(issues);
  return ok({ valueMs, reason, kind, fromMs: currentTtlMs(view) });
}

// ---- screen state: stale reads, response ordering, freshness (Codex r1 P2-6, r3 P2-5) ----------------------------

/** How long a successful read may be shown as effective and acted on before it must be read again. */
export const TTL_VIEW_FRESHNESS_MS = 30_000;

export interface TtlScreenState {
  /** The last successful read, if any. */
  readonly view: SessionRevocationTtlView | undefined;
  /** The latest completed read failed. `view` (if any) is outdated: shown as such, never acted on. */
  readonly stale: boolean;
  readonly error: ApiError | undefined;
  /** The newest read started, and the read whose outcome is shown. Changes wait until they match. */
  readonly latestGeneration: number;
  readonly appliedGeneration: number;
  /** When the shown view was read, and when the last read was attempted (client clock, ms). */
  readonly loadedAtMs: number | undefined;
  readonly lastAttemptAtMs: number | undefined;
}

export function initialTtlScreenState(): TtlScreenState {
  return { view: undefined, stale: false, error: undefined, latestGeneration: 0, appliedGeneration: 0, loadedAtMs: undefined, lastAttemptAtMs: undefined };
}

/** Record that read `generation` started at `nowMs` (generations are increasing per screen). */
export function startTtlLoad(state: TtlScreenState, generation: number, nowMs: number): TtlScreenState {
  return generation > state.latestGeneration ? { ...state, latestGeneration: generation, lastAttemptAtMs: nowMs } : state;
}

/** Apply read `generation`'s outcome at `nowMs`. An older response never overwrites a newer read; a failure marks the view stale. */
export function finishTtlLoad(state: TtlScreenState, generation: number, result: Result<SessionRevocationTtlView, ApiError>, nowMs: number): TtlScreenState {
  if (generation < state.latestGeneration) return state;
  return result.ok
    ? { ...state, view: result.value, stale: false, error: undefined, appliedGeneration: generation, loadedAtMs: nowMs }
    : { ...state, stale: true, error: result.error, appliedGeneration: generation };
}

/** True while `nowMs - fromMs` is a finite, non-negative age below `limitMs` (a clock stepping back is never "fresh"). */
const within = (fromMs: number | undefined, nowMs: number, limitMs: number): boolean => {
  if (fromMs === undefined) return false;
  const age = nowMs - fromMs;
  return Number.isFinite(age) && age >= 0 && age < limitMs;
};

/** The shown view may be presented as effective: a successful read younger than the freshness deadline. */
export function isTtlViewFresh(state: TtlScreenState, nowMs: number): boolean {
  return state.view !== undefined && !state.stale && within(state.loadedAtMs, nowMs, TTL_VIEW_FRESHNESS_MS);
}

/** Changes (propose/approve/reject) are allowed only on a fresh read with no newer read in flight. */
export function canChangeTtl(state: TtlScreenState, nowMs: number): boolean {
  return isTtlViewFresh(state, nowMs) && state.appliedGeneration === state.latestGeneration;
}

/** A reload is due when no read is in flight and the last attempt is at least one freshness period old (or never ran). */
export function needsTtlRefresh(state: TtlScreenState, nowMs: number): boolean {
  if (state.appliedGeneration !== state.latestGeneration) return false;
  return !within(state.lastAttemptAtMs, nowMs, TTL_VIEW_FRESHNESS_MS);
}

/** Whether the facts an operator decided on changed between two reads (current value/version/validity or the pending request). */
export function ttlDecisionBasisChanged(before: SessionRevocationTtlView, after: SessionRevocationTtlView): boolean {
  const basis = (v: SessionRevocationTtlView): string =>
    v.effective.valid
      ? `valid:${v.effective.source}:${v.effective.version ?? '-'}:${v.effective.valueMs}`
      : `invalid:${v.effective.version ?? '-'}:${v.effective.enforcedValueMs}`;
  return basis(before) !== basis(after) || (before.pending?.id ?? null) !== (after.pending?.id ?? null);
}

// ---- pending approval ------------------------------------------------------------------------------------

export interface PendingActions {
  /** A different operator may approve. Unknown identity → offered; the server refuses a self-approval. */
  readonly canApprove: boolean;
  readonly canReject: boolean;
  /** The signed-in operator raised this request (their reject is recorded as a withdrawal). */
  readonly isOwnRequest: boolean;
}

/** D868: `selfApproval` (a GLOBAL super administrator, `maySelfApprove`) offers Approve on one's own request too. */
export function pendingActionsFor(pending: GovernedSettingChangeView | null, operatorId: string | undefined, selfApproval = false): PendingActions {
  if (pending === null) return { canApprove: false, canReject: false, isOwnRequest: false };
  const own = operatorId !== undefined && pending.requestedBy === operatorId;
  return { canApprove: !own || selfApproval, canReject: true, isOwnRequest: own };
}

export const CHANGE_STATUS_TONE: Readonly<Record<GovernedChangeStatus, 'neutral' | 'success' | 'warning' | 'danger'>> = {
  PENDING_APPROVAL: 'warning',
  APPLIED: 'success',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
  SUPERSEDED: 'neutral',
};

/** Milliseconds with locale digit grouping (the chrome language), e.g. `5000` → `5,000` in `en`. */
export function formatMs(ms: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(ms);
}

// ---- write outcome notice (Codex r4 P2-2) -------------------------------------------------------------------

/** The Admin message a committed governance write shows, with its toast tone. */
export interface TtlWriteNotice {
  readonly key: 'sec.toast.applied' | 'sec.toast.approved' | 'sec.toast.appliedNotLive' | 'sec.toast.pending' | 'sec.toast.rejected' | 'sec.toast.withdrawn';
  readonly tone: 'success' | 'warning';
}

/**
 * The notice for a committed governance write. An APPLIED change that the answering replica could not re-read
 * (`refreshDegraded`) is committed but NOT yet enforced there, so it is a warning, never the plain success. An applied
 * loosening can only come from an approval, so the direction picks "approved" versus "applied".
 */
export function ttlWriteNotice(response: GovernedChangeResponse<'APPLIED' | 'PENDING_APPROVAL' | 'REJECTED' | 'WITHDRAWN'>): TtlWriteNotice {
  switch (response.outcome) {
    case 'APPLIED':
      if (response.refreshDegraded === true) return { key: 'sec.toast.appliedNotLive', tone: 'warning' };
      return { key: response.change.direction === 'LOOSEN' ? 'sec.toast.approved' : 'sec.toast.applied', tone: 'success' };
    case 'PENDING_APPROVAL':
      return { key: 'sec.toast.pending', tone: 'success' };
    case 'REJECTED':
      return { key: 'sec.toast.rejected', tone: 'success' };
    case 'WITHDRAWN':
      return { key: 'sec.toast.withdrawn', tone: 'success' };
  }
}

// ---- runtime helpers (thin seams over the real operator endpoints) ------------------------------------------

interface SecurityRuntime {
  readonly securitySettings: SecuritySettingsApi;
}

export function loadSessionRevocationTtl(runtime: SecurityRuntime): Promise<Result<SessionRevocationTtlView, ApiError>> {
  return runtime.securitySettings.sessionRevocationTtl();
}

/** The write helpers keep the whole response, `refreshDegraded` included (Codex r4 P2-2): show it with `ttlWriteNotice`. */
export function proposeSessionRevocationTtl(runtime: SecurityRuntime, input: ProposeSessionRevocationTtlInput): Promise<Result<GovernedChangeResponse<'APPLIED' | 'PENDING_APPROVAL'>, ApiError>> {
  return runtime.securitySettings.proposeSessionRevocationTtl({ valueMs: input.valueMs, reason: input.reason });
}

export function approveSessionRevocationTtl(runtime: SecurityRuntime, changeId: string, reason: string): Promise<Result<GovernedChangeResponse<'APPLIED'>, ApiError>> {
  return runtime.securitySettings.approveSessionRevocationTtl(changeId, { reason });
}

export function rejectSessionRevocationTtl(runtime: SecurityRuntime, changeId: string, reason: string): Promise<Result<GovernedChangeResponse<'REJECTED' | 'WITHDRAWN'>, ApiError>> {
  return runtime.securitySettings.rejectSessionRevocationTtl(changeId, { reason });
}
